import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Tooltip, CircularProgress, Menu, MenuItem, IconButton } from '@mui/material';
import { TrendingUp as TrendingUpIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';

interface TokenData {
  id: string;
  symbol: string;
  usd: number;
  usd_24h_change: number;
  icon: string;
}

export default function NavbarPriceDisplay() {
  const [tokens, setTokens] = useState<TokenData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleTokens, setVisibleTokens] = useState<string[]>(['ethereum', 'bitcoin', 'tether', 'binancecoin', 'solana']);

  const fetchData = async () => {
    try {
      // Use CryptoCompare API which has excellent CORS support
      const response = await fetch('https://min-api.cryptocompare.com/data/pricemultifull?fsyms=ETH,BTC,USDT,BNB,SOL&tsyms=USD');
      const data = await response.json();
      
      
      
      const tokenList: TokenData[] = [
        {
          id: 'ethereum',
          symbol: 'ETH',
          usd: data.RAW?.ETH?.USD?.PRICE || 0,
          usd_24h_change: data.RAW?.ETH?.USD?.CHANGEPCT24HOUR || 0,
          icon: 'Ξ'
        },
        {
          id: 'bitcoin',
          symbol: 'BTC',
          usd: data.RAW?.BTC?.USD?.PRICE || 0,
          usd_24h_change: data.RAW?.BTC?.USD?.CHANGEPCT24HOUR || 0,
          icon: '₿'
        },
        {
          id: 'tether',
          symbol: 'USDT',
          usd: data.RAW?.USDT?.USD?.PRICE || 0,
          usd_24h_change: data.RAW?.USDT?.USD?.CHANGEPCT24HOUR || 0,
          icon: '₮'
        },
        {
          id: 'binancecoin',
          symbol: 'BNB',
          usd: data.RAW?.BNB?.USD?.PRICE || 0,
          usd_24h_change: data.RAW?.BNB?.USD?.CHANGEPCT24HOUR || 0,
          icon: '◈'
        },
        {
          id: 'solana',
          symbol: 'SOL',
          usd: data.RAW?.SOL?.USD?.PRICE || 0,
          usd_24h_change: data.RAW?.SOL?.USD?.CHANGEPCT24HOUR || 0,
          icon: '◎'
        }
      ];
      
      setTokens(tokenList);
      
      
      
    } catch (e) {
      
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number) => {
    if (price < 0.01) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 6,
        maximumFractionDigits: 6
      }).format(price);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleToken = (tokenId: string) => {
    setVisibleTokens(prev => 
      prev.includes(tokenId) 
        ? prev.filter(id => id !== tokenId)
        : [...prev, tokenId]
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" sx={{ display: { xs: 'block', sm: 'block' } }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error || tokens.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="error">
          {error || 'No price data'}
        </Typography>
      </Box>
    );
  }

  const visibleTokenData = tokens.filter(token => visibleTokens.includes(token.id));
  
  

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: { xs: 0.5, sm: 1 }, 
      flexWrap: 'nowrap',
      maxWidth: '100%',
      overflow: 'auto',
      '&::-webkit-scrollbar': { display: 'none' },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none'
    }}>
      {visibleTokenData.map((token, index) => (
        <Tooltip key={token.id} title={`${token.symbol} Price`} arrow>
          <Chip
            icon={<TrendingUpIcon />}
            label={`${token.symbol} ${formatPrice(token.usd)}`}
            size="small"
            color={token.usd_24h_change >= 0 ? 'success' : 'error'}
            variant="filled"
            sx={{ 
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              height: { xs: 18, sm: 24 },
              '& .MuiChip-icon': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
              fontWeight: 'bold',
              bgcolor: token.usd_24h_change >= 0 ? 'success.main' : 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: token.usd_24h_change >= 0 ? 'success.dark' : 'error.dark',
              },
              flexShrink: 0,
              minWidth: { xs: 'auto', sm: 'auto' },
              maxWidth: { xs: '120px', sm: 'none' }
            }}
          />
        </Tooltip>
      ))}
      
      <IconButton
        size="small"
        onClick={handleMenuClick}
        sx={{ 
          p: { xs: 0.25, sm: 0.5 },
          display: { xs: 'flex', md: 'flex' },
          flexShrink: 0
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            maxHeight: { xs: 300, sm: 400 },
            width: { xs: '90vw', sm: 'auto' },
            maxWidth: { xs: '90vw', sm: 300 }
          }
        }}
      >
        {tokens.map((token) => (
          <MenuItem 
            key={token.id}
            onClick={() => {
              toggleToken(token.id);
              handleMenuClose();
            }}
            selected={visibleTokens.includes(token.id)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 0.5, sm: 1 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {token.symbol}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {formatPrice(token.usd)}
              </Typography>
              <Chip
                label={`${token.usd_24h_change >= 0 ? '+' : ''}${token.usd_24h_change.toFixed(2)}%`}
                color={token.usd_24h_change >= 0 ? 'success' : 'error'}
                size="small"
                sx={{ ml: 'auto', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
} 